// Pitch Deck Extraction Types and Helpers
// The actual extraction is done via edge function (extractPitchDeck in edge-functions.ts)

import type { StartupProfile } from '@/lib/state/profile-store';

export interface ExtractedField {
  value: string | number | string[];
  confidence: number; // 0-100
}

export interface ExtractedStartupData {
  name: ExtractedField;
  tagline: ExtractedField;
  industry: ExtractedField;
  stage: ExtractedField;
  problem: ExtractedField;
  solution: ExtractedField;
  targetCustomer: ExtractedField;
  marketSize: ExtractedField;
  fundingTarget: ExtractedField;
  teamSize: ExtractedField;
  competitors: ExtractedField;
  location: ExtractedField;
  foundedYear: ExtractedField;
  overallConfidence: number;
}

/** Parse a user-entered numeric field → positive integer or `undefined`.
 * Strips grouping separators; a zero/blank/non-numeric value is UNKNOWN
 * (undefined), never a silent default. */
function toPositiveNumber(value: string | undefined): number | undefined {
  const n = Number((value ?? '').replace(/[^0-9]/g, ''));
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

/**
 * Build a StartupProfile from the reviewed/edited pitch-deck fields.
 *
 * Task 3: this MUST NOT fabricate values the founder never confirmed. Fields
 * the user left blank stay UNKNOWN (`undefined`/`null`) — no `funding_target: 0`,
 * no `founded_year: <current year>`, no `team_size: 1`, no
 * `is_actively_raising: false`. Those silent defaults polluted scoring inputs.
 * Kept pure (no store/React deps) so the no-default guarantee is unit-testable.
 */
export function buildReviewedDeckProfile(fieldValues: Record<string, string>): StartupProfile {
  const competitors = fieldValues.competitors
    ? fieldValues.competitors.split(',').map((c) => c.trim()).filter(Boolean)
    : undefined;

  return {
    id: `startup-${Date.now()}`,
    name: fieldValues.name ?? '',
    tagline: fieldValues.tagline ?? '',
    industry: fieldValues.industry ?? '',
    // Unknown stage stays null (not 'pre-seed').
    stage: ((fieldValues.stage as StartupProfile['stage']) || null) as StartupProfile['stage'],
    fundingTarget: toPositiveNumber(fieldValues.fundingTarget),
    location: fieldValues.location ?? '',
    foundedYear: toPositiveNumber(fieldValues.foundedYear),
    teamSize: toPositiveNumber(fieldValues.teamSize),
    profileSource: 'deck',
    problem: fieldValues.problem,
    solution: fieldValues.solution,
    targetCustomer: fieldValues.targetCustomer,
    marketSize: toPositiveNumber(fieldValues.marketSize),
    competitors,
    dataRoomConnected: false,
    isSerialFounder: false,
  } as unknown as StartupProfile;
}

/**
 * Get confidence level label
 */
export function getConfidenceLevel(confidence: number): 'high' | 'medium' | 'low' {
  if (confidence >= 80) return 'high';
  if (confidence >= 60) return 'medium';
  return 'low';
}

/**
 * Get confidence color
 */
export function getConfidenceColor(confidence: number): string {
  if (confidence >= 80) return '#34C759'; // green
  if (confidence >= 60) return '#FF9500'; // amber
  return '#FF3B30'; // red
}

/**
 * Mock extraction for demo mode
 * Returns randomized sample data to simulate AI extraction
 */
export function getMockExtraction(): ExtractedStartupData {
  const mockData: ExtractedStartupData[] = [
    {
      name: { value: 'TechFlow AI', confidence: 95 },
      tagline: { value: 'Automating workflows with intelligent AI agents', confidence: 88 },
      industry: { value: 'AI / ML', confidence: 92 },
      stage: { value: 'seed', confidence: 85 },
      problem: {
        value: 'Businesses waste 30% of employee time on repetitive tasks that could be automated.',
        confidence: 78,
      },
      solution: {
        value: 'TechFlow AI provides no-code AI agents that learn from watching employees work.',
        confidence: 82,
      },
      targetCustomer: {
        value: 'Mid-market companies (100-1000 employees) in finance and healthcare',
        confidence: 75,
      },
      marketSize: { value: 15000000000, confidence: 70 },
      fundingTarget: { value: 2500000, confidence: 90 },
      teamSize: { value: 8, confidence: 95 },
      competitors: { value: ['Zapier', 'UiPath', 'Automation Anywhere'], confidence: 72 },
      location: { value: 'San Francisco, CA', confidence: 88 },
      foundedYear: { value: 2024, confidence: 92 },
      overallConfidence: 83,
    },
    {
      name: { value: 'GreenCart', confidence: 92 },
      tagline: { value: 'Sustainable grocery delivery for conscious consumers', confidence: 85 },
      industry: { value: 'E-commerce', confidence: 90 },
      stage: { value: 'pre-seed', confidence: 88 },
      problem: {
        value: 'Grocery delivery creates massive plastic waste and carbon emissions.',
        confidence: 80,
      },
      solution: {
        value: 'GreenCart delivers groceries in reusable containers with electric vehicles.',
        confidence: 85,
      },
      targetCustomer: {
        value: 'Urban millennials and Gen-Z consumers who prioritize sustainability',
        confidence: 78,
      },
      marketSize: { value: 8500000000, confidence: 65 },
      fundingTarget: { value: 750000, confidence: 92 },
      teamSize: { value: 4, confidence: 90 },
      competitors: { value: ['Instacart', 'Amazon Fresh', 'Imperfect Foods'], confidence: 75 },
      location: { value: 'Austin, TX', confidence: 85 },
      foundedYear: { value: 2025, confidence: 95 },
      overallConfidence: 81,
    },
  ];

  // Return random mock with slight confidence jitter
  const base = mockData[Math.floor(Math.random() * mockData.length)];
  const jitter = () => Math.floor(Math.random() * 10) - 5;

  return {
    ...base,
    name: { ...base.name, confidence: Math.min(100, Math.max(50, base.name.confidence + jitter())) },
    tagline: { ...base.tagline, confidence: Math.min(100, Math.max(50, base.tagline.confidence + jitter())) },
    overallConfidence: Math.min(100, Math.max(60, base.overallConfidence + jitter())),
  };
}
